import { motion } from "framer-motion";
import { ShieldCheck, HeartHandshake, Clock, LifeBuoy, Compass } from "lucide-react";
import { METER_META } from "@/lib/sim";
import { RankBadge } from "@/components/sim/RankBadge";

const ICONS = { ShieldCheck, HeartHandshake, Clock, LifeBuoy };

const Meter = ({ id, value }) => {
  const meta = METER_META[id];
  const Icon = ICONS[meta.icon];
  return (
    <div data-testid={`meter-${id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className={`grid place-items-center h-7 w-7 rounded-lg bg-white ring-1 ${meta.ring} ${meta.text}`}>
            <Icon className="h-4 w-4" strokeWidth={2} />
          </span>
          <span className="text-sm font-medium text-stone-700">{meta.label}</span>
        </div>
        <span data-testid={`meter-${id}-value`} className={`text-sm font-semibold tabular-nums ${meta.text}`}>
          {Math.round(value)}
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={Math.round(value)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${meta.label}: ${Math.round(value)} out of 100`}
        className="h-2.5 w-full rounded-full bg-stone-200 overflow-hidden"
      >
        <motion.div
          className={`h-full rounded-full ${meta.bar}`}
          initial={false}
          animate={{ width: `${Math.max(0, Math.min(100, value))}%` }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        />
      </div>
      <p className="mt-1 text-[11px] leading-tight text-stone-400">{meta.hint}</p>
    </div>
  );
};

export const MeterPanel = ({ meters, timeLabel, lens, rank }) => {
  return (
    <aside data-testid="status-panel" className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6 shadow-sm">
      {rank && (
        <div className="pb-4 mb-4 border-b border-stone-100">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">Your rank</span>
          <div className="mt-2"><RankBadge rank={rank} /></div>
        </div>
      )}
      <div className="flex items-center justify-between pb-4 border-b border-stone-100">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">Shift</span>
        <strong data-testid="time-label" className="font-heading text-sm text-stone-800">{timeLabel}</strong>
      </div>
      <div className="mt-5 space-y-5">
        {["safety", "trust", "time", "resources"].map((id) => (
          <Meter key={id} id={id} value={meters[id]} />
        ))}
      </div>
      <div className="mt-6 rounded-xl bg-stone-50 border border-stone-100 p-4">
        <div className="flex items-center gap-2 text-stone-700">
          <Compass className="h-4 w-4 text-emerald-700" />
          <span className="text-[11px] font-semibold uppercase tracking-wider">Training lens</span>
        </div>
        <p data-testid="training-lens" className="mt-2 text-sm leading-relaxed text-stone-600">{lens}</p>
      </div>
    </aside>
  );
};
